import type { ReactNode } from 'react';
import { formatPrice } from '../../../../modules/currency';
import {
  CENTER_LABEL_SIZE,
  CENTER_VALUE_SIZE,
  FONT,
  LABEL_COLOR,
  LABEL_SIZE,
  VALUE_COLOR,
  VALUE_SIZE,
} from './constants';
import { useFitSideText } from './useFitSideText';

export interface StatsSummaryProps {
  /** Floor of the cheapest tier — leftmost stat. */
  minValue: number;
  /** Expected / estimated payout — centre stat, largest type. */
  maxPull: number;
  /** Right-hand column content — the "View Odds / Normal ⌄" trigger
   *  (a button in the live block, a static label inside the modal recap). */
  rightSlot: ReactNode;
  /** Extra classes on the root. Consumers set the horizontal padding so the
   *  row lines up with their surrounding content — the wider glass block
   *  (`px-5`) vs the modal where it spans the full bar-column width. */
  className?: string;
}

/**
 * Three-zone pack-stats content — Figma `1336:86230`. Flex layout (NOT
 * absolute) so it never overflows: the two side columns reserve their real
 * (nowrap) width first (`shrink-0`), the centre hero takes the leftover
 * (`flex-1`) and auto-shrinks its font to that width via `useFitTextToWidth`.
 * So a long figure (e.g. "$7,500") can never push the "View Odds" column off
 * its box and collide with it on a narrow phone — it shrinks instead.
 *
 * Pure layout, NO glass background of its own, so it sits either inside
 * the glass `OddsPanel` block OR inside the `OddsModal` card.
 */
export function StatsSummary({ minValue, maxPull, rightSlot, className = '' }: StatsSummaryProps) {
  const minLabel = formatPrice(minValue, { max: 1 });
  const maxLabel = formatPrice(maxPull);
  const { leftColRef, leftInnerRef, rightColRef, rightInnerRef, scale, heroScale } = useFitSideText(
    `${minLabel}|${maxLabel}`,
  );
  // Sides shrink by `scale` (drives the fit); the hero shrinks gentler by
  // `heroScale` so it stays the focal figure without dwarfing the rest. Pinned
  // to each block's outer/bottom corner so it collapses toward the row edge /
  // centre and keeps its baseline aligned.
  const leftStyle = scale < 1 ? { transform: `scale(${scale})`, transformOrigin: 'left bottom' } : undefined;
  const rightStyle = scale < 1 ? { transform: `scale(${scale})`, transformOrigin: 'right bottom' } : undefined;
  const heroStyle = scale < 1 ? { transform: `scale(${heroScale})`, transformOrigin: 'center bottom' } : undefined;
  return (
    <div className={`flex flex-col gap-2 ${className}`} style={{ fontFamily: FONT }}>
      {/* Top — "Max Pull" flanked by decorative dividers. */}
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 rounded-[30px] bg-[#d9d9d9] opacity-20" />
        <span
          className="whitespace-nowrap text-center font-semibold leading-[22px] tracking-[-0.3px]"
          style={{ color: VALUE_COLOR, fontSize: CENTER_LABEL_SIZE }}
        >
          Max Pull
        </span>
        <span className="h-px flex-1 rounded-[30px] bg-[#d9d9d9] opacity-20" />
      </div>

      {/* Bottom — Min Value | Max Pull hero | rightSlot. The hero keeps its
          full size; the two side blocks scale DOWN (one shared factor) to the
          width flex left them, so a long figure can't be ridden over by the
          "View Odds" text. */}
      <div className="flex items-end gap-3">
        <div ref={leftColRef} className="flex min-w-0 flex-1 flex-col items-start gap-[2px]">
          <div ref={leftInnerRef} className="flex flex-col items-start gap-[2px]" style={leftStyle}>
            <span
              className="whitespace-nowrap font-medium leading-[20px] tracking-[-0.1px]"
              style={{ color: LABEL_COLOR, fontSize: LABEL_SIZE }}
            >
              Min Value
            </span>
            <span
              className="whitespace-nowrap font-bold leading-[28px] tracking-[-0.56px]"
              style={{ color: VALUE_COLOR, fontSize: VALUE_SIZE }}
            >
              {minLabel}
            </span>
          </div>
        </div>

        <span
          className="shrink-0 whitespace-nowrap text-center font-bold leading-[40px] tracking-[-0.96px]"
          style={{ color: VALUE_COLOR, fontSize: CENTER_VALUE_SIZE, ...heroStyle }}
        >
          {maxLabel}
        </span>

        <div ref={rightColRef} className="flex min-w-0 flex-1 flex-col items-end gap-[2px]">
          <div ref={rightInnerRef} className="flex flex-col items-end gap-[2px]" style={rightStyle}>
            {rightSlot}
          </div>
        </div>
      </div>
    </div>
  );
}
